import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Plus, Trash2, Download, Calendar, Users, Zap, TrendingUp, MapPin } from 'lucide-react';
import StationBuilder from './StationBuilder';
import DemoLimitsInfo from './DemoLimitsInfo';
import ScaleToFit from './ScaleToFit';
import { getAuthConfig } from '../utils/auth';
import { captureCard } from '../utils/captureCard';
import {
  createDemoEvent,
  deleteDemoEvent,
  getDemoEvents,
  getDemoMessages,
  isDemoUser,
  updateDemoEvent,
} from '../utils/demoSession';

const EventsDashboard = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState(null);
  const [showStationBuilder, setShowStationBuilder] = useState(false);
  const [selectedEventForStations, setSelectedEventForStations] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    recipient_name: '',
    recipient_id: '',
    release_date: '',
    release_time: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    recipient_name: '',
    release_date: '',
    release_time: ''
  });
  const demoMode = isDemoUser(user);
  const demoMessageCount = demoMode ? getDemoMessages(user).length : 0;

  useEffect(() => {
    if (user?.email) {
      loadEvents();
    }
  }, [demoMode, user]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      if (demoMode) {
        const demoEvents = getDemoEvents(user);
        setEvents(demoEvents);
        console.log('✅ Demo events loaded:', demoEvents);
        return;
      }

      const response = await axios.get('/api/events', getAuthConfig());
      let events = response.data.events || [];
      
      // If demo mode and no events, add a mock one
      if (user?.isDemo === true && events.length === 0) {
        const mockEvent = {
          id: 'demo-evt-1',
          name: '🎉 Beispiel-Event',
          description: 'Dies ist ein Beispiel-Event',
          recipient_name: 'Best Friend',
          release_date: '2030-01-01',
          release_time: '12:00',
          created_at: new Date().toISOString(),
          isDemo: true,
          event_qr_token: 'demo-token'
        };
        events = [mockEvent];
      }
      
      setEvents(events);
      console.log('✅ Events loaded:', events);
    } catch (error) {
      console.error('❌ Error loading events:', error);
      if (!demoMode) {
        alert('Fehler beim Laden der Events');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Event-Name erforderlich');
      return;
    }

    try {
      if (demoMode) {
        createDemoEvent(user, formData);
        setFormData({ name: '', description: '', recipient_name: '', recipient_id: '', release_date: '', release_time: '' });
        setShowEventForm(false);
        alert('✅ Demo-Event erfolgreich erstellt!');
        loadEvents();
        return;
      }

      const response = await axios.post('/api/events/create', {
        name: formData.name,
        description: formData.description,
        recipient_name: formData.recipient_name,
        recipient_id: formData.recipient_id || null,
        release_date: formData.release_date || null,
        release_time: formData.release_time || null
      }, getAuthConfig());

      console.log('✅ Event created:', response.data);
      setFormData({ name: '', description: '', recipient_name: '', recipient_id: '', release_date: '', release_time: '' });
      setShowEventForm(false);
      alert('✅ Event erfolgreich erstellt!');
      loadEvents();
    } catch (error) {
      console.error('❌ Error creating event:', error);
      alert('Fehler beim Erstellen des Events');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Event wirklich löschen?')) {
      try {
        if (demoMode) {
          deleteDemoEvent(user, eventId);
          setEvents(events.filter(e => e.id !== eventId));
          alert('✅ Demo-Event gelöscht!');
          return;
        }

        await axios.delete(`/api/events/${eventId}`, getAuthConfig());
        setEvents(events.filter(e => e.id !== eventId));
        alert('✅ Event gelöscht!');
      } catch (error) {
        console.error('❌ Error deleting event:', error);
        alert('Fehler beim Löschen des Events');
      }
    }
  };

  const handleStartEdit = (event) => {
    setSelectedEventForEdit(event);
    setEditFormData({
      name: event.name,
      description: event.description,
      recipient_name: event.recipient_name || '',
      release_date: event.release_date || '',
      release_time: event.release_time || ''
    });
    setShowEditForm(true);
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    if (!editFormData.name) {
      alert('Event-Name erforderlich');
      return;
    }

    try {
      if (demoMode) {
        updateDemoEvent(user, selectedEventForEdit.id, {
          name: editFormData.name,
          description: editFormData.description,
          recipient_name: editFormData.recipient_name,
          release_date: editFormData.release_date || null,
          release_time: editFormData.release_time || null
        });
        setShowEditForm(false);
        alert('✅ Demo-Event erfolgreich aktualisiert!');
        loadEvents();
        return;
      }

      await axios.put(`/api/events/${selectedEventForEdit.id}`, {
        name: editFormData.name,
        description: editFormData.description,
        recipient_name: editFormData.recipient_name,
        release_date: editFormData.release_date || null,
        release_time: editFormData.release_time || null
      }, getAuthConfig());

      console.log('✅ Event updated');
      setShowEditForm(false);
      alert('✅ Event erfolgreich aktualisiert!');
      loadEvents();
    } catch (error) {
      console.error('❌ Error updating event:', error);
      alert('Fehler beim Aktualisieren des Events');
    }
  };

  const downloadEventQR = async (event) => {
    const element = document.getElementById(`event-card-${event.id}`);
    if (!element) {
      alert('Eventkarte nicht gefunden – bitte Modal öffnen');
      return;
    }
    try {
      const canvas = await captureCard(element);
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `timelock_event_${event.name.replace(/\s+/g, '_')}_${Date.now()}.png`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, 'image/png', 1);
    } catch (err) {
      alert('Download fehlgeschlagen: ' + err.message);
    }
  };

  const copyEventLink = (event) => {
    const link = `${window.location.origin}/event/${event.event_qr_token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // Calculate stats
  const totalEvents = events.length;
  const totalStations = events.reduce((sum, e) => sum + (e.station_count || 0), 0);
  const activeEvents = events.length;

  // Get color for event card
  const getCardColor = (index) => {
    const colors = [
      'from-purple-600 to-purple-700',
      'from-blue-600 to-blue-700',
      'from-pink-600 to-pink-700',
      'from-orange-600 to-orange-700',
      'from-teal-600 to-teal-700',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"></div>
          </div>
          <p className="text-slate-300">⏳ Events werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Demo Mode Info */}
        <DemoLimitsInfo user={user} messageCount={demoMessageCount} eventCount={events.length} />
        
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
            <div>
              <h1 className="text-3xl sm:text-5xl font-bold text-white mb-2">📍 Schnitzeljagden</h1>
              <p className="text-slate-400 text-base sm:text-lg">Erstelle unvergessliche Erlebnisse für deine Hinterbliebenen</p>
            </div>
            <button
              onClick={() => setShowEventForm(!showEventForm)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
            >
              <Plus size={24} /> Neue Schnitzeljagd
            </button>
          </div>

          {/* Create Event Form */}
          {showEventForm && (
            <div className="bg-slate-800/50 backdrop-blur-md border border-purple-500/30 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Neue Schnitzeljagd erstellen</h2>
              <form onSubmit={handleCreateEvent} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-slate-300 mb-2 font-semibold">Event-Name *</label>
                  <input
                    type="text"
                    placeholder="z.B. Roadtrip Hamburg, Geburtstagsjagd"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-slate-300 mb-2 font-semibold">Beschreibung</label>
                  <textarea
                    placeholder="Beschreibe das Event und seine Geschichte..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Für wen? *</label>
                  <input
                    type="text"
                    placeholder="z.B. Max, Mein Sohn, Maria"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Empfänger-ID (optional)</label>
                  <input
                    type="text"
                    placeholder="Empfänger ID..."
                    value={formData.recipient_id}
                    onChange={(e) => setFormData({ ...formData, recipient_id: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabedatum (optional)</label>
                  <input
                    type="date"
                    value={formData.release_date}
                    onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabeuhrzeit (optional)</label>
                  <input
                    type="time"
                    value={formData.release_time}
                    onChange={(e) => setFormData({ ...formData, release_time: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div className="md:col-span-2 flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    Schnitzeljagd erstellen
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEventForm(false);
                      setFormData({ name: '', description: '', recipient_name: '', recipient_id: '' });
                    }}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Main Grid: Events Cards + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Events Cards - Left Side (Takes 2 columns on large screens) */}
          <div className="lg:col-span-2">
            {events.length === 0 ? (
              <div className="bg-slate-800/50 backdrop-blur-md border-2 border-dashed border-purple-500/30 rounded-2xl p-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-slate-400" />
                <p className="text-slate-400 text-lg mb-2">Noch keine Schnitzeljagden erstellt.</p>
                <p className="text-slate-500">Starten Sie eine neue Schnitzeljagd um unvergessliche Momente zu bewahren!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer transform transition-all hover:scale-105 hover:shadow-2xl bg-gradient-to-br ${getCardColor(index)} group`}
                  >
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-16 -mb-16"></div>

                    {/* Card Content */}
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold mb-2">{event.name}</h3>
                      {event.recipient_name && (
                        <p className="text-sm text-white/80 mb-3">👤 für <strong>{event.recipient_name}</strong></p>
                      )}
                      <p className="text-sm text-white/70 mb-4 line-clamp-2">{event.description}</p>

                      {/* Stats */}
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                          <Zap size={14} /> {event.station_count} Stationen
                        </div>
                        <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                          <Calendar size={14} /> {new Date(event.created_at).toLocaleDateString('de-DE')}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                          }}
                          className="flex-1 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Eventkarte & QR-Code anzeigen"
                        >
                          📥 QR
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEventForStations(event);
                            setShowStationBuilder(true);
                          }}
                          className="flex-1 bg-blue-600/50 hover:bg-blue-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Stationen verwalten"
                        >
                          🗺️ Stationen
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(event);
                          }}
                          className="flex-1 bg-yellow-600/50 hover:bg-yellow-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Event bearbeiten"
                        >
                          ✏️ Bearbeiten
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event.id);
                          }}
                          className="flex-1 bg-red-600/50 hover:bg-red-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Event löschen"
                        >
                          🗑️ Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Statistics Panel - Right Side */}
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="bg-slate-800/50 backdrop-blur-md border border-purple-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm">Gesamt Schnitzeljagden</p>
                  <p className="text-4xl font-bold text-white">{totalEvents}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center text-2xl">
                  📍
                </div>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-md border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Stationen gesamt</p>
                  <p className="text-4xl font-bold text-white">{totalStations}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center text-2xl">
                  🎯
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-md border border-green-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Aktiv heute</p>
                  <p className="text-4xl font-bold text-white">{activeEvents}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center text-2xl">
                  ✨
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-md border border-slate-700 rounded-2xl p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <TrendingUp size={18} /> Schnellzugriff
              </h3>
              <div className="space-y-2">
                {events.slice(0, 3).map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className="w-full text-left px-4 py-2 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-sm text-slate-300 transition-all"
                  >
                    <span className="font-semibold">{event.name}</span>
                    <span className="block text-xs text-slate-400">→ {event.station_count} Stationen</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Events Section */}
        {events.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-md border border-slate-700 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Calendar size={24} /> Alle Schnitzeljagden
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400">Name</th>
                    <th className="text-left py-3 px-4 text-slate-400">Für</th>
                    <th className="text-left py-3 px-4 text-slate-400">Stationen</th>
                    <th className="text-left py-3 px-4 text-slate-400">Erstellt</th>
                    <th className="text-right py-3 px-4 text-slate-400">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-all cursor-pointer" onClick={() => setSelectedEvent(event)}>
                      <td className="py-3 px-4 text-white font-semibold">{event.name}</td>
                      <td className="py-3 px-4 text-slate-300">{event.recipient_name || '—'}</td>
                      <td className="py-3 px-4 text-slate-300">{event.station_count}</td>
                      <td className="py-3 px-4 text-slate-400 text-xs">{new Date(event.created_at).toLocaleDateString('de-DE')}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyEventLink(event);
                          }}
                          className="text-xs bg-purple-600/50 hover:bg-purple-600 px-3 py-1 rounded transition-all"
                          title="Link kopieren"
                        >
                          {copiedLink ? '✓' : '📋'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => {
            setSelectedEvent(null);
            setCopiedLink(false);
          }}
        >
          <div
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-white mb-2">{selectedEvent.name}</h2>
            {selectedEvent.recipient_name && (
              <p className="text-purple-400 mb-4 text-lg">📍 für <strong>{selectedEvent.recipient_name}</strong></p>
            )}
            <p className="text-slate-300 mb-6">{selectedEvent.description}</p>

            {/* Link Section */}
            <div className="mb-6 bg-slate-700/50 p-4 rounded-xl border border-slate-600">
              <p className="text-slate-300 mb-2 text-sm font-semibold">🔗 Schnitzeljagd-Link:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/event/${selectedEvent.event_qr_token}`}
                  className="flex-1 px-3 py-2 bg-slate-600 text-slate-300 text-sm rounded border border-slate-500 focus:outline-none"
                />
                <button
                  onClick={() => copyEventLink(selectedEvent)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded transition-all"
                >
                  {copiedLink ? '✓ Kopiert!' : '📋 Kopieren'}
                </button>
              </div>
            </div>

            {/* Eventkarte */}
            <div className="mb-6">
              <p className="text-slate-300 mb-3 font-semibold">📱 Eventkarte für Empfänger:</p>
              <ScaleToFit width={608} height={380}>
                <div
                  id={`event-card-${selectedEvent.id}`}
                  style={{
                    background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
                    border: '3px solid #8b7355',
                    borderRadius: '0px',
                    padding: '28px 32px',
                    color: '#2d2d2d',
                    fontFamily: 'Georgia, serif',
                    position: 'relative',
                    width: '100%',
                    boxSizing: 'border-box',
                    aspectRatio: '16 / 10',
                    display: 'flex',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  }}
                >
                  <div style={{ position: 'absolute', left: '50%', top: '28px', bottom: '28px', width: '2px', background: '#8b7355', pointerEvents: 'none' }} />

                  {/* LEFT: QR */}
                  <div style={{ position: 'absolute', left: '32px', top: '28px', bottom: '28px', width: 'calc(50% - 48px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ background: 'white', padding: '10px', border: '3px solid #999', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', marginTop: '16px' }}>
                      <QRCodeCanvas
                        value={`${window.location.origin}/event/${selectedEvent.event_qr_token}`}
                        size={160}
                        level="H"
                        includeMargin={false}
                      />
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.4', marginBottom: '4px' }}>
                      <strong style={{ color: '#8b5a3c', fontWeight: '700' }}>Hinweis:</strong> QR-Code mit Kamera erfassen
                    </div>
                  </div>

                  {/* RIGHT: Text */}
                  <div style={{ position: 'absolute', left: 'calc(50% + 18px)', right: '32px', top: '28px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', minWidth: 0 }}>
                      <div style={{ fontSize: '30px', fontWeight: 'normal', color: '#8b5a3c', letterSpacing: '2px', fontStyle: 'italic', fontFamily: "'Brush Script MT', 'Great Vibes', cursive", lineHeight: '1.0', flex: 1, minWidth: 0 }}>
                        EventCARD
                      </div>
                      <div style={{ width: '50px', height: '58px', border: '2px solid #8b7355', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', backgroundColor: 'rgba(255,255,255,0.1)', flexShrink: 0, marginLeft: '8px' }}>
                        🔐
                      </div>
                    </div>
                    <p style={{ fontSize: '18px', fontStyle: 'italic', color: '#2d2d2d', margin: '0 0 8px 0', fontWeight: '700', borderBottom: '2px solid #2d2d2d', paddingBottom: '4px', display: 'inline-block' }}>
                      {selectedEvent.name}
                    </p>
                    {selectedEvent.release_date && (
                      <p style={{ fontSize: '13px', color: '#8b5a3c', fontStyle: 'italic', margin: '10px 0 0 0' }}>
                        📅 {new Date(selectedEvent.release_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                      <p style={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic', margin: 0 }}>
                        🔒 time-lock.de — Digitale Momente, die bleiben.
                      </p>
                    </div>
                  </div>
                </div>
              </ScaleToFit>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => downloadEventQR(selectedEvent)}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded-lg transition-all"
              >
                📥 QR-Code herunterladen
              </button>
              <button
                onClick={() => {
                  setSelectedEvent(null);
                  setCopiedLink(false);
                }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Station Builder Modal */}
      {showStationBuilder && selectedEventForStations && (
        <StationBuilder
          eventId={selectedEventForStations.id}
          userEmail={user.email}
          user={user}
          onClose={() => {
            setShowStationBuilder(false);
            setSelectedEventForStations(null);
            loadEvents(); // Refresh events to show updated station counts
          }}
        />
      )}

      {/* Edit Event Modal */}
      {showEditForm && selectedEventForEdit && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowEditForm(false)}
        >
          <div
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-white mb-6">Event bearbeiten: {selectedEventForEdit.name}</h2>
            
            <form onSubmit={handleEditEvent} className="space-y-6">
              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Event-Name *</label>
                <input
                  type="text"
                  placeholder="z.B. Roadtrip Hamburg"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Beschreibung</label>
                <textarea
                  placeholder="Beschreibe das Event und seine Geschichte..."
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  rows="4"
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Für wen?</label>
                <input
                  type="text"
                  placeholder="z.B. Max, Mein Sohn, Maria"
                  value={editFormData.recipient_name}
                  onChange={(e) => setEditFormData({ ...editFormData, recipient_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabedatum</label>
                  <input
                    type="date"
                    value={editFormData.release_date}
                    onChange={(e) => setEditFormData({ ...editFormData, release_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabeuhrzeit</label>
                  <input
                    type="time"
                    value={editFormData.release_time}
                    onChange={(e) => setEditFormData({ ...editFormData, release_time: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white font-bold py-3 rounded-lg transition-all"
                >
                  ✅ Speichern
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsDashboard;
